import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { clsx } from 'clsx'

import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@quebon/ui/components/dialog'
import { CtaButton } from '../CtaButton'
import type { ConsentTermsContent } from './consent.content'

interface ConsentTermsModalProps {
  open: boolean
  onClose: () => void
  content: ConsentTermsContent
}

export function ConsentTermsModal({
  open,
  onClose,
  content,
}: ConsentTermsModalProps) {
  const { t: mt } = useTranslation('marketing')

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose()
      }}
    >
      {open && (
        <div
          aria-hidden
          className="fixed inset-0 z-[1100] bg-black/50 backdrop-blur-sm"
        />
      )}
      <DialogContent
        showCloseButton={false}
        className={clsx(
          'w-full max-w-[calc(100%-2rem)] sm:max-w-[440px] max-h-[80vh] overflow-hidden',
          'flex flex-col p-0 gap-0',
          'bg-white rounded-[20px] shadow-[0px_8px_24px_0px_rgba(16,24,40,0.16)]',
          'ring-0 text-inherit',
          'z-[1101]',
        )}
        style={{ zIndex: 1101 }}
      >
        <div className="flex items-start justify-between pt-6 pb-3 px-7 shrink-0">
          <DialogTitle className="text-[18px] font-bold text-qb-text-high leading-[1.4] tracking-[-0.4px]">
            {content.title}
          </DialogTitle>
          <button
            type="button"
            aria-label={mt('certificate.close')}
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-[8px] bg-[#f7f7f7] text-qb-text-low hover:text-qb-text-high transition-colors shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 px-7 pb-4 flex flex-col gap-4 text-qb-text-high text-[14px] leading-[1.5]">
          {content.intro && (
            <p className="text-qb-text-mid">{content.intro}</p>
          )}

          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr>
                {content.table.headers.map((h) => (
                  <th
                    key={h}
                    className="border border-qb-divider bg-qb-bg-secondary px-2 py-2 font-semibold text-center"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {content.table.rows.map((row, ri) => (
                <tr key={ri}>
                  {row.columns.map((cell, ci) => (
                    <td
                      key={ci}
                      className="border border-qb-divider px-2 py-2 align-top"
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          <ul className="text-[12px] text-qb-text-mid flex flex-col gap-1">
            {content.footnotes.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
        </div>

        <div className="shrink-0 px-7 pt-3 pb-6">
          <CtaButton
            type="button"
            variant="primary"
            size="md"
            fullWidth
            onClick={onClose}
          >
            {mt('consent.confirm')}
          </CtaButton>
        </div>
      </DialogContent>
    </Dialog>
  )
}
